import { creatorbase } from '@round/api';
import { getTableMetaHelper, Image, SkeletonizedValue } from '@round/ui-kit';
import { CellContext } from '@tanstack/react-table';
import styles from './ProjectCell.module.css';
import { Link } from 'react-router-dom';

export type ProjectData = {
    name: string;
    brandName: string;
    brandImage: string | undefined | null;
    teamName: string;
    link?: string;
    image?: string | null | undefined;
};

type Row = creatorbase.PublicCampaign;
type Context<T extends Row> = CellContext<T, ProjectData>;

export type Meta<T extends Row = Row> = {
    isLoading: boolean;
    getIsProjectLoading: (row: T) => boolean;
    getIsBrandLoading: (row: T) => boolean;
    getIsTeamLoading: (row: T) => boolean;
};

const getTableMeta = getTableMetaHelper<Meta>();

const ProjectCell = <T extends Row>({ getValue, table, row: { original } }: Context<T>) => {
    const { isLoading, getIsProjectLoading, getIsBrandLoading, getIsTeamLoading } = getTableMeta(table);
    const isDataLoading =
        isLoading || getIsProjectLoading(original) || getIsBrandLoading(original) || getIsTeamLoading(original);

    const { brandName, name, teamName, link, image } = getValue();
    const projectNameElement = (
        <SkeletonizedValue width="10rem" isInitialized={!isDataLoading}>
            {name || '-'}
        </SkeletonizedValue>
    );

    return (
        <div className={styles.projectContainer}>
            <Image skeletonCircle={false} loading={isDataLoading} src={image || ''} className={styles.projectImage} />
            <div className={styles.projectDetails}>
                {link ? (
                    <Link to={link} className={styles.projectName} onClick={(e) => e.stopPropagation()}>
                        {projectNameElement}
                    </Link>
                ) : (
                    <p className={styles.projectName}>{projectNameElement}</p>
                )}

                <p className={styles.brandName}>
                    <SkeletonizedValue width="7rem" isInitialized={!isDataLoading}>
                        {brandName} · {teamName}
                    </SkeletonizedValue>
                </p>
            </div>
        </div>
    );
};

export default ProjectCell;
